import React from 'react';
import {
  TextField, IconButton, Tooltip,
  InputAdornment, Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { DataTable, type DataTableColumn } from '../../../components/common';
import { EditableExamRow } from '../providerExamRows';

interface Props {
  rows: EditableExamRow[];
  disabled?: boolean;
  canRemove?: boolean;
  onChange: (examId: number, patch: Partial<EditableExamRow>) => void;
  onRemove: (row: EditableExamRow) => void;
}

const reaisToCents = (v: number | null) => (v == null ? 0 : Math.round(v * 100));
const formatCents = (cents: number): string => {
  const reais = Math.floor(cents / 100);
  const cent = cents % 100;
  return `${reais.toLocaleString('pt-BR')},${String(cent).padStart(2, '0')}`;
};
const parseCents = (text: string): number => {
  const digits = text.replace(/\D/g, '');
  const cents = parseInt(digits || '0', 10);
  return cents > 9999999 ? 9999999 : cents;
};

export const ProviderExamsTable: React.FC<Props> = ({ rows, disabled, canRemove = true, onChange, onRemove }) => {
  const moneyField = (row: EditableExamRow, field: 'price' | 'cost') => {
    const isPending = row._origin === 'new' || row._dirty;
    const priceErr = field === 'price' && isPending && (row.price == null || row.price <= 0);
    const costErr = field === 'cost' && row.cost != null && row.cost < 0;
    return (
      <TextField
        value={row[field] == null ? '' : formatCents(reaisToCents(row[field]))}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '');
          onChange(row.examId, { [field]: digits === '' ? null : parseCents(e.target.value) / 100 } as Partial<EditableExamRow>);
        }}
        size="small" inputMode="numeric" placeholder="0,00" disabled={disabled}
        error={priceErr || costErr}
        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
        sx={{ width: 120 }}
      />
    );
  };

  const columns: DataTableColumn<EditableExamRow>[] = [
    {
      id: 'exam',
      label: 'Exame',
      primary: true,
      render: (row) => (
        <>
          <Typography variant="body2">{row.examName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[row.esocialCode && `eSocial: ${row.esocialCode}`, row.tussCode && `TUSS: ${row.tussCode}`].filter(Boolean).join(' | ')}
          </Typography>
        </>
      ),
    },
    { id: 'price', label: 'Valor', render: (row) => moneyField(row, 'price') },
    { id: 'cost', label: 'Custo', render: (row) => moneyField(row, 'cost') },
    {
      id: 'turnaroundDays',
      label: 'Prazo (dias)',
      render: (row) => (
        <TextField
          value={row.turnaroundDays ?? ''}
          onChange={(e) => {
            const raw = e.target.value.replace(/\D/g, '');
            const num = raw ? Math.min(parseInt(raw, 10), 90) : null;
            onChange(row.examId, { turnaroundDays: num });
          }}
          size="small" inputMode="numeric" placeholder="—" disabled={disabled} sx={{ width: 90 }}
        />
      ),
    },
    {
      id: 'observations',
      label: 'Observações',
      render: (row) => (
        <TextField
          value={row.observations ?? ''}
          onChange={(e) => onChange(row.examId, { observations: e.target.value || null })}
          size="small" fullWidth placeholder="—" disabled={disabled}
        />
      ),
    },
    {
      id: 'remove',
      label: '',
      align: 'right',
      render: (row) => (
        <Tooltip title="Remover exame">
          <span>
            <IconButton
              color="error"
              size="small"
              disabled={disabled || (row._origin === 'existing' && !canRemove)}
              onClick={() => onRemove(row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      getRowId={(row) => row.examId}
      emptyState={{
        title: 'Nenhum exame adicionado',
        description: 'Os exames deste prestador entram pela busca logo acima.',
      }}
    />
  );
};

export default ProviderExamsTable;
